import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { ThemeOptions } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import { ROARING_FONTS as fonts } from "../constants";

const rv = (key: string) => `var(--mui-palette-roaring-${key})`;

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    "text-box": true;
    subtitle3: true;
    subTitle5: true;
    label: true;
    subtitle4: true;
    entityHeader: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    roaring: {
      drawerBackground: string;
      green: string;
      darkGreen: string;
      primaryBrandColor: string;
      linkColor: string;
      blue: string;

      // neutrals / borders / misc
      gray: string;
      lightGray: string;
      border: string;
      borderStrong: string;
      hoverColor: string;
      icon: string;

      // surfaces
      backgroundColor: string;
      paper: string;
      paperAlt: string;

      // component-specific
      inputBg: string;
      snackbarBg: string;
      autocompleteBg: string;

      // text (explicit, since your dark theme differs)
      textPrimary: string;
      textSecondary: string;
      textDisabled: string;
    };
  }

  interface PaletteOptions {
    roaring?: Partial<Palette["roaring"]>;
  }
}

export const lightColors = {
  drawerBackground: "#eff4f3",
  green: "#33625C",
  darkGreen: "#006666",
  primaryBrandColor: "#1B5048",
  linkColor: "#0E7FAA",
  blue: "#334288",
  gray: "rgba(0,0,0,0.7)",
  lightGray: "rgba(0,0,0,0.26)",
  border: "rgba(0,0,0,0.7)",
  orange: "#F2832C",
  red: "#DA2128",
  pink: "#C13F81",
  hoverColor: "rgba(18,130,115,0.04)",
  backgroundColor: "#e0e9e8",
  icon: "#272727",
};

export const darkColors = {
  green: "#719f9a",
  darkGreen: "#006666",
  primaryBrandColor: "#7cd4c7",
  blue: "#334288",
  pink: "#C13F81",
  orange: "#F2832C",
  red: "#DA2128",
  linkColor: "#4db6e6",

  backgroundColor: "#0B1212",
  drawerBackground: "#0F1918",
  paper: "#111D1B",
  paperAlt: "#0E1716",

  textPrimary: "rgba(255,255,255,0.92)",
  textSecondary: "rgba(255,255,255,0.72)",
  textDisabled: "rgba(255,255,255,0.42)",
  border: "rgba(255,255,255,0.14)",
  borderStrong: "rgba(255,255,255,0.20)",
  hoverColor: "rgba(18,130,115,0.18)",
  icon: "rgba(255,255,255,0.88)",
};

const BORDER_RADIUS = 8;

const shared: ThemeOptions = {
  cssVariables: {
    colorSchemeSelector: "data", // important so toggling updates [data-mui-color-scheme]
  },
  shape: { borderRadius: BORDER_RADIUS },

  typography: {
    fontFamily: fonts,
    subtitle1: {
      fontWeight: 500,
      fontSize: "1rem",
      lineHeight: 1.5,
      letterSpacing: "0.00938em",
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: "0.8125rem",
      lineHeight: 1.43,
      letterSpacing: "0.00938em",
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        body: {
          // CSS-vars friendly: these swap automatically with the scheme
          backgroundColor: theme.vars.palette.background.default,
          color: theme.vars.palette.text.primary,
        },
      }),
    },

    MuiAccordionSummary: {
      defaultProps: { expandIcon: <ExpandMoreIcon /> },
      styleOverrides: {
        root: () => ({
          height: 48,
          "&.Mui-expanded": { minHeight: 48 },
          "&:hover": { backgroundColor: rv("hoverColor") },
        }),
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        elevation1: ({ theme }) => ({
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 0 20px rgba(0,0,0,0.55)"
              : "0 0 20px rgba(0,0,0,0.2)",
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--mui-palette-roaring-inputBg)",
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small", margin: "dense" },
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            backgroundColor: "var(--mui-palette-roaring-inputBg)",
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          overflow: "visible",
          flexShrink: 0,
          whiteSpace: "nowrap",
          backgroundColor: "var(--mui-palette-roaring-drawerBackground)",
          borderRight: `1px solid ${theme.vars.palette.divider}`,
          backgroundImage: "none",
        }),
        paperAnchorDockedLeft: { boxShadow: "none", borderWidth: 0 },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: ({ theme }) => ({ color: theme.vars.palette.text.primary }),
        inset: { paddingLeft: 42 },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 42,
          color: rv("primaryBrandColor"),
        },
      },
    },

    MuiIconButton: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: () => ({ color: rv("primaryBrandColor") }),
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: rv("linkColor"),
          fontFamily: fonts,
          fontWeight: 400,
          textDecoration: "none",
          "&:hover": { cursor: "pointer", textDecoration: "underline" },
        },
        button: { textAlign: "left", display: "block" },
      },
    },

    MuiTable: { defaultProps: { size: "small" } },

    MuiButton: {
      defaultProps: {
        size: "small",
        disableElevation: true,
      },

      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS,
          whiteSpace: "nowrap",
        },

        sizeSmall: {
          height: 36,
        },
      },
    },

    MuiSnackbarContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${rv("borderStrong")}`,
          backgroundColor: rv("snackbarBg"),
          color: theme.vars.palette.text.secondary,
          padding: "0 16px",
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: "none",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          marginRight: 16,
          marginBottom: 16,
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: rv("autocompleteBg"),
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 0 20px rgba(0,0,0,0.55)"
              : "0 0 20px rgba(0,0,0,0.2)",
        }),
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          overflowX: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        sizeSmall: { padding: "6px 8px" },
        head: {
          whiteSpace: "nowrap",
          paddingTop: 0,
          textOverflow: "clip",
          paddingBottom: 0,
          fontSize: "0.813rem",
        },
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-hover": { cursor: "pointer" },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&:last-child .MuiTableCell-body": { borderBottomWidth: 0 },
          borderColor:
            theme.palette.mode === "dark" ? rv("border") : rv("gray"),
        }),
        head: { backgroundColor: "transparent", height: 50 },
        footer: { borderWidth: "1px 0 0 0", borderStyle: "solid" },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          "& th:first-of-type": { borderTopLeftRadius: 8 },
          "& th:last-child": { borderTopRightRadius: 8 },
        },
      },
    },

    MuiTablePagination: { styleOverrides: { root: { borderWidth: 0 } } },

    MuiList: { defaultProps: { disablePadding: true } },

    MuiMenu: {
      styleOverrides: {
        paper: { transition: "none !important" },
        list: { maxHeight: 300 },
      },
    },

    MuiChip: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        avatar: {
          backgroundColor: "transparent !important",
        },
        icon: {
          color: "inherit",
        },
        root: {
          fontWeight: 500,
          "& $avatarColorPrimary": {
            backgroundColor: "transparent",
          },
          "& $avatarColorSecondary": {
            backgroundColor: "transparent",
          },
        },
      },
      variants: [
        // ACTIVE
        {
          props: { variant: "filled", color: "primary" },
          style: ({ theme }) => ({
            color: "#1E4620",
            backgroundColor: "#DCF4C1",
            ...theme.applyStyles("dark", {
              color: "#CFF7D1",
              backgroundColor: "rgba(30,70,32,0.45)",
            }),
          }),
        },

        // INFO
        {
          props: { variant: "filled", color: "info" },
          style: ({ theme }) => ({
            color: "#185272",
            backgroundColor: "#E5F5FD",
            ...theme.applyStyles("dark", {
              color: "#BFE9FF",
              backgroundColor: "rgba(24,82,114,0.45)",
            }),
          }),
        },
        {
          props: { variant: "filled", color: "secondary" },
          style: ({ theme }) => ({
            color: "#fff",
            backgroundColor: "#C13F81",
            ...theme.applyStyles("dark", {
              color: "#fff",
              backgroundColor: "#C13F81",
            }),
          }),
        },

        // WARNING
        {
          props: { variant: "filled", color: "warning" },
          style: ({ theme }) => ({
            color: "#7F3E0C",
            backgroundColor: "#FFF2D3",
            ...theme.applyStyles("dark", {
              color: "#FFE6C7",
              backgroundColor: "rgba(127,62,12,0.45)",
            }),
          }),
        },

        // FAILED
        {
          props: { variant: "filled", color: "error" },
          style: ({ theme }) => ({
            color: "#9B0417",
            backgroundColor: "#FFE2E2",
            ...theme.applyStyles("dark", {
              color: "#FFD0D0",
              backgroundColor: "rgba(155,4,23,0.42)",
            }),
          }),
        },
      ],
    },
    MuiTypography: {
      variants: [
        // ─────────────────────────────────────────────
        // Base text-box
        // ─────────────────────────────────────────────
        {
          props: { variant: "text-box" },
          style: ({ theme }) => ({
            fontFamily: fonts,
            minWidth: 100,

            // light default
            backgroundColor: "var(--mui-palette-background-paper)",
            color: "var(--mui-palette-text-primary)",

            // dark override
            ...theme.applyStyles("dark", {
              backgroundColor: "var(--mui-palette-roaring-paperAlt)",
              color: "var(--mui-palette-roaring-textPrimary)",
            }),
          }),
        },

        // ─────────────────────────────────────────────
        // ACTIVE
        // ─────────────────────────────────────────────
        {
          props: { variant: "text-box", color: "active" },
          style: ({ theme }) => ({
            color: "#1E4620",
            backgroundColor: "#DCF4C1",

            ...theme.applyStyles("dark", {
              color: "#CFF7D1",
              backgroundColor: "rgba(30,70,32,0.45)",
            }),
          }),
        },

        // ─────────────────────────────────────────────
        // INFO
        // ─────────────────────────────────────────────
        {
          props: { variant: "text-box", color: "info" },
          style: ({ theme }) => ({
            color: "#185272",
            backgroundColor: "#E5F5FD",

            ...theme.applyStyles("dark", {
              color: "#BFE9FF",
              backgroundColor: "rgba(24,82,114,0.45)",
            }),
          }),
        },

        // ─────────────────────────────────────────────
        // WARNING
        // ─────────────────────────────────────────────
        {
          props: { variant: "text-box", color: "warning" },
          style: ({ theme }) => ({
            color: "#7F3E0C",
            backgroundColor: "#FFF2D3",

            ...theme.applyStyles("dark", {
              color: "#FFE6C7",
              backgroundColor: "rgba(127,62,12,0.45)",
            }),
          }),
        },

        // ─────────────────────────────────────────────
        // FAILED
        // ─────────────────────────────────────────────
        {
          props: { variant: "text-box", color: "failed" },
          style: ({ theme }) => ({
            color: "#9B0417",
            backgroundColor: "#FFE2E2",

            ...theme.applyStyles("dark", {
              color: "#FFD0D0",
              backgroundColor: "rgba(155,4,23,0.42)",
            }),
          }),
        },

        // ─────────────────────────────────────────────
        // DISABLED
        // ─────────────────────────────────────────────
        {
          props: { variant: "text-box", color: "disabled" },
          style: ({ theme }) => ({
            color: "#5D5D5D",
            backgroundColor: "#F4F6FA",

            ...theme.applyStyles("dark", {
              color: "rgba(255,255,255,0.65)",
              backgroundColor: "rgba(255,255,255,0.06)",
            }),
          }),
        },

        // ─────────────────────────────────────────────
        // SANCTIONS
        // ─────────────────────────────────────────────
        {
          props: { variant: "text-box", color: "sanctions" },
          style: ({ theme }) => ({
            fontFamily: fonts,
            color: "#7F3E0C",
            backgroundColor: "#FFF2D3",

            ...theme.applyStyles("dark", {
              color: "#FFE6C7",
              backgroundColor: "rgba(127,62,12,0.45)",
            }),
          }),
        },
      ],
    },
  },
};

/**
 * 2) Single theme with two schemes.
 *    MUI switches these automatically and useColorScheme reads/sets mode.
 */
export const theme = createTheme({
  ...shared,

  colorSchemes: {
    light: {
      palette: {
        primary: { main: lightColors.green, contrastText: "#fff" },
        secondary: { main: lightColors.blue, contrastText: "#fff" },
        error: { main: lightColors.red, contrastText: "#fff" },
        warning: { main: lightColors.orange, contrastText: "#fff" },

        background: {
          default: lightColors.backgroundColor,
          paper: "#ffffff",
        },

        text: {
          primary: lightColors.icon,
          secondary: lightColors.gray,
          disabled: lightColors.lightGray,
        },

        divider: lightColors.border,

        roaring: {
          ...lightColors,
          // fill required extras for light scheme
          paper: "#ffffff",
          paperAlt: "#ffffff",
          textPrimary: lightColors.icon,
          textSecondary: lightColors.gray,
          textDisabled: lightColors.lightGray,
          borderStrong: "rgba(0,0,0,0.26)",
          inputBg: "#ffffff",
          snackbarBg: "#ffffff",
          autocompleteBg: lightColors.backgroundColor,
        },
      },
    },

    dark: {
      palette: {
        primary: { main: darkColors.green, contrastText: "#fff" },
        secondary: { main: darkColors.blue, contrastText: "#fff" },
        error: { main: darkColors.red, contrastText: "#fff" },
        warning: { main: darkColors.orange, contrastText: "#111" },

        background: {
          default: darkColors.backgroundColor,
          paper: darkColors.paper,
        },

        text: {
          primary: darkColors.textPrimary,
          secondary: darkColors.textSecondary,
          disabled: darkColors.textDisabled,
        },

        divider: darkColors.border,

        roaring: {
          ...darkColors,
          inputBg: darkColors.paperAlt,
          snackbarBg: darkColors.paper,
          autocompleteBg: darkColors.paper,
        },
      },
    },
  },
});
